'use client';

import clsx from 'clsx';
import { useMetrica } from 'next-yandex-metrica';
import { useState } from 'react';

import {
	Checkbox,
	LogoSquare,
	MailIcon,
	TextFiled,
	useConfirm,
} from '@/shared';
import {
	consentOfProcessingPersonalDataDocument,
	consentToAdvertisingMailingDocument,
	ofertaDocument,
	orderOfProcessingPersonalDataDocument,
	userAgreementDocument,
} from '@/shared/constants';
import { useUpdate } from '@/shared/hooks';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';

import { AuthByLogin } from './auth-by-login-form';

export interface PaymentAccountValues {
	userEmail?: string;
	advertisingMail?: boolean;
	isAgree?: boolean;
}

interface PaymentAccountFormProps {
	values: PaymentAccountValues;
	errors?: { userEmail?: string[] };
	updateValues: (values: PaymentAccountValues) => void;
	goNextStep: () => void;
	onEvent?: (action: string, outcome?: string) => void;
}

export const PaymentAccountForm = ({
	values,
	errors,
	updateValues,
	goNextStep,
	onEvent,
}: PaymentAccountFormProps) => {
	const confirm = useConfirm();
	const { reachGoal } = useMetrica();
	const [email, setEmail] = useState(values.userEmail || '');
	const [advertisingMail, setAdvertisingMail] = useState(false);

	const [confirmPolicy, setConfirmPolicy] = useState(false);
	const [confirmOffert, setConfirmOffert] = useState(false);

	const handleSubmit = () => {
		updateValues({ userEmail: email });
		goNextStep();
	};

	const handleAfterAuth = () => {
		window.location.reload();
	};

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newEmail = e.target.value;
		setEmail(newEmail);
		updateValues({ userEmail: newEmail });
	};

	const handleAdvertisingMailChange = async (
		e: React.ChangeEvent<HTMLInputElement>,
	) => {
		const advertisingMail = e.target.checked;

		// Если пытаемся снять галочку
		if (!advertisingMail) {
			// reachGoal('confirmAdvertisingMail');
			const confirmed = await confirm('', {
				title: 'Мы не шлём спам',
				description:
					'В рассылках мы делимся популярными задачами с собеседований за текущий месяц, даём персональные рекомендации и уведомляем о скидках и акциях. Отписаться можно в любой момент.',
				confirmText: 'Получать полезные материалы',
				cancelText: 'Всё равно отказаться',
			});

			if (confirmed) {
				onEvent?.('advertising_retained');
				e.target.checked = true;
				return;
			} else {
				try {
					reachGoal('uncheckAdvertisingMail');
				} catch {
					/* Analytics must not block checkout. */
				}
			}
		}

		setAdvertisingMail(advertisingMail);
		updateValues({ advertisingMail: advertisingMail });
		onEvent?.('advertising_change', advertisingMail ? 'accepted' : 'declined');
	};

	useUpdate(() => {
		updateValues({ isAgree: confirmPolicy && confirmOffert });
	}, [confirmPolicy, confirmOffert]);

	const [active, setActive] = useState('register');
	const tabs = [
		{
			key: 'login',
			text: 'Вход',
		},
		{
			key: 'register',
			text: 'Создать аккаунт',
		},
	];

	return (
		<Card className="my-6 flex w-full max-w-lg flex-col gap-8 rounded-xl !px-6 !py-10 md:my-10 md:min-h-min md:bg-white md:!p-14">
			<div className="grid grid-cols-2">
				{tabs.map((tab) => (
					<button
						key={tab.key}
						type="button"
						aria-pressed={active === tab.key}
						className={clsx('cursor-pointer border-b py-3 text-center', {
							'border-b-accent text-black': active === tab.key,
							'border-b-gray-4 text-gray-3': active !== tab.key,
						})}
						onClick={() => {
							setActive(tab.key);
							onEvent?.('account_tab', tab.key);
						}}
					>
						{tab.text}
					</button>
				))}
			</div>

			{active === 'login' && (
				<div className="flex flex-col gap-8">
					<AuthByLogin onSuccess={handleAfterAuth} onEvent={onEvent} />
				</div>
			)}

			{active === 'register' && (
				<div className="flex flex-col gap-8">
					<div className="flex items-center justify-center">
						<LogoSquare />
					</div>
					<div className="text-center">
						<h2 className="mb-2 text-2xl font-bold text-gray-1">
							Укажите ваш email
						</h2>
						<p className="text-gray-3">
							Мы отправим вам информацию о курсе и доступе к обучению
						</p>
					</div>

					<div className="flex flex-col gap-8">
						<TextFiled
							type="email"
							label="Email адрес"
							icon={<MailIcon className="size-6" />}
							placeholder="Введите ваш email"
							value={email}
							onChange={handleEmailChange}
							onBlur={() => onEvent?.('email_complete')}
							autoComplete="email"
							invalid={!!errors?.userEmail}
							hint={errors?.userEmail ? errors.userEmail.join(', ') : undefined}
						/>

						<fieldset className="space-y-2">
							<Checkbox
								name="confirmPolicy"
								value="true"
								label={
									<>
										Я принимаю условия{' '}
										<a
											href={orderOfProcessingPersonalDataDocument.url}
											onClick={() =>
												onEvent?.('account_document_open', 'privacy')
											}
											className="text-accent hover:underline"
											target="_blank"
										>
											Политики обработки персональных данных
										</a>{' '}
										и{' '}
										<a
											href={userAgreementDocument.url}
											onClick={() =>
												onEvent?.('account_document_open', 'agreement')
											}
											className="text-accent hover:underline"
											target="_blank"
										>
											Пользовательского соглашения
										</a>{' '}
										и даю свое{' '}
										<a
											href={consentOfProcessingPersonalDataDocument.url}
											onClick={() =>
												onEvent?.('account_document_open', 'consent')
											}
											className="text-accent hover:underline"
											target="_blank"
										>
											согласие на обработку персональных данных
										</a>
									</>
								}
								invalid={!confirmPolicy}
								onChange={(e) => {
									setConfirmPolicy(e.target.checked);
									onEvent?.(
										'policy_change',
										e.target.checked ? 'accepted' : 'declined',
									);
								}}
								defaultChecked={confirmPolicy}
								labelSize="xs"
							/>
							<Checkbox
								name="confirmOffert"
								value="true"
								label={
									<>
										Я согласен с условиями{' '}
										<a
											href={ofertaDocument.url}
											onClick={() =>
												onEvent?.('account_document_open', 'offer')
											}
											className="text-accent hover:underline"
											target="_blank"
										>
											Оферты
										</a>
									</>
								}
								invalid={!confirmOffert}
								onChange={(e) => {
									setConfirmOffert(e.target.checked);
									onEvent?.(
										'terms_change',
										e.target.checked ? 'accepted' : 'declined',
									);
								}}
								defaultChecked={confirmOffert}
								labelSize="xs"
							/>
							<Checkbox
								name="advertisingMail"
								value="true"
								label={
									<>
										Я{' '}
										<a
											href={consentToAdvertisingMailingDocument.url}
											onClick={() =>
												onEvent?.('account_document_open', 'advertising')
											}
											className="text-accent hover:underline"
											target="_blank"
										>
											согласен
										</a>{' '}
										получать рассылку с полезными материалами и анонсами.
										Никакого спама, отписаться можно в 1 клик
									</>
								}
								onChange={handleAdvertisingMailChange}
								defaultChecked={advertisingMail}
								labelSize="xs"
							/>
						</fieldset>

						<Button
							block
							onClick={handleSubmit}
							type="button"
							disabled={!email || !confirmPolicy || !confirmOffert}
							size="lg"
						>
							Продолжить
						</Button>
					</div>
				</div>
			)}
		</Card>
	);
};
